/**
 * DesignDayGroup Component
 *
 * A collapsible group of design days by type (heating, cooling, etc.).
 */

import { useState } from 'react';
import { DesignDayItem } from './DesignDayItem';
import type { DesignDayMetadata, ParsedDesignDay, DesignDayType } from '../../types';

export interface DesignDayGroupProps {
  type: DesignDayType;
  designDays: Array<DesignDayMetadata | ParsedDesignDay>;
  selectedIds: Set<string>;
  recommendedIds: string[];
  onToggle: (id: string) => void;
  onViewDetails: (designDay: DesignDayMetadata | ParsedDesignDay) => void;
}

/**
 * Get display name and icon for design day type.
 */
function getTypeInfo(type: DesignDayType): { name: string; color: string } {
  switch (type) {
    case 'heating':
      return { name: 'Heating Design Days', color: 'text-blue-600 dark:text-blue-400' };
    case 'cooling':
      return { name: 'Cooling Design Days', color: 'text-red-600 dark:text-red-400' };
    case 'extreme':
      return { name: 'Extreme Conditions', color: 'text-orange-600 dark:text-orange-400' };
    case 'dehumidification':
      return { name: 'Dehumidification', color: 'text-cyan-600 dark:text-cyan-400' };
    case 'humidification':
      return { name: 'Humidification', color: 'text-teal-600 dark:text-teal-400' };
    case 'other':
      return { name: 'Other Conditions', color: 'text-gray-600 dark:text-gray-400' };
    default:
      return { name: 'Design Days', color: 'text-gray-600' };
  }
}

export function DesignDayGroup({
  type,
  designDays,
  selectedIds,
  recommendedIds,
  onToggle,
  onViewDetails,
}: DesignDayGroupProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const typeInfo = getTypeInfo(type);

  // Count selected in this group
  const selectedCount = designDays.filter((dd) => selectedIds.has(dd.id)).length;

  if (designDays.length === 0) {
    return null;
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between bg-muted/50 px-4 py-3 hover:bg-muted transition-colors"
      >
        <div className="flex items-center gap-2">
          {/* Chevron */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          >
            <path d="m9 18 6-6-6-6" />
          </svg>

          {/* Type name */}
          <span className={`font-medium ${typeInfo.color}`}>{typeInfo.name}</span>

          {/* Count badge */}
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {designDays.length}
          </span>
        </div>

        {/* Selection count */}
        {selectedCount > 0 && (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            {selectedCount} selected
          </span>
        )}
      </button>

      {/* Items */}
      {isExpanded && (
        <div className="space-y-2 p-3">
          {designDays.map((dd) => (
            <DesignDayItem
              key={dd.id}
              designDay={dd}
              isSelected={selectedIds.has(dd.id)}
              isRecommended={recommendedIds.includes(dd.id)}
              onToggle={() => onToggle(dd.id)}
              onViewDetails={() => onViewDetails(dd)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
